import {graphql} from 'react-apollo';
import {compose, withHandlers, withStateHandlers, withState} from 'recompose';
import {connect} from 'react-redux';
import reduxForm from 'redux-form/lib/reduxForm';
import find from 'lodash/find';
import formValueSelector from 'redux-form/lib/formValueSelector';
import SubmissionError from 'redux-form/lib/SubmissionError';
import loadingSpinner from '../../hoc/loadingSpinner';
import {setView as setTicketView} from '../../../tickets/actions/list';
import {setView as setEquipmentView} from '../../../equipments/actions/list';
import {
  DELETE_TICKETVIEW_MUTATION,
  SAVE_TICKETVIEW_MUTATION,
  TICKET_VIEWS_QUERY
} from '../../../tickets/queries';
import {
  DELETE_EQUIPMENTVIEW_MUTATION,
  SAVE_EQUIPMENTVIEW_MUTATION,
  EQUIPMENT_VIEWS_QUERY
} from '../../../equipments/queries';
import {injectIntl} from 'react-intl';

// this one is a little messy because it had some hard dependencies on the ticket queries
// the equipment stuff has been jammed in there but it's not generic enough
// should hold up OK for now but if we ever need to do a third type of view we need to extract
// this mess into a generic container (maybe moving this into exportpanelcontainer?)

function validate(values) {
  if (!values.name) return {name: 'Required'};
  return {};
}
const ACTIONS = {
  "tickets": {
    "setView": setTicketView
  },
  "equipments": {
    "setView": setEquipmentView
  }
}
const FORM_NAMES = {
  "tickets": 'TicketSavedView',
  "equipments": 'EquipmentSavedView'
}
const QUERIES = {
  "tickets": {
    "get": TICKET_VIEWS_QUERY,
    "save": SAVE_TICKETVIEW_MUTATION,
    "delete": DELETE_TICKETVIEW_MUTATION
  },
  "equipments": {
    "get": EQUIPMENT_VIEWS_QUERY,
    "save": SAVE_EQUIPMENTVIEW_MUTATION,
    "delete": DELETE_EQUIPMENTVIEW_MUTATION
  }
}

export default viewType => compose(
  injectIntl,
  // state for the save / load panels
  withStateHandlers(
    {isSavePanelOpen: false, isLoadPanelOpen: false},
    {
      openSavePanel: () => () => ({
        isSavePanelOpen: true,
        isLoadPanelOpen: false
      }),
      openLoadPanel: () => () => ({
        isSavePanelOpen: false,
        isLoadPanelOpen: true
      }),
      closePanel: () => e => {
        if (e) e.preventDefault();
        return {isSavePanelOpen: false, isLoadPanelOpen: false};
      }
    }
  ),

  // query the existing views, with their metadata.
  // We do that when showing the component, this way they won't have to wait if they want to use one.
  graphql(QUERIES[viewType].get, {
    props: ({data}) => ({
      loading: data.loading,
      error: data.error,
      savedViews: (viewType === 'tickets' ? data.ticketViews : data.equipmentViews) || []
    })
  }),
  loadingSpinner,

  // redux data and actions
  connect(
    state => ({
      includeFilters: formValueSelector(FORM_NAMES[viewType])(state, 'includeFilters')
    }),
    {
      setView: ACTIONS[viewType].setView
    }
  ),

  // handler for applying a view
  withState('selectedView', 'setSelectedView', ''),
  withStateHandlers(
    {selectedView: ''},
    {
      setSelectedView: () => e => ({
        selectedView: e.target ? e.target.value : e
      })
    }
  ),
  withHandlers({
    useView: ({setView, savedViews, selectedView}) => e => {
      if (selectedView) {
        const view = find(savedViews, v => v.name === selectedView);
        if (view && view.definition) {
          setView(JSON.parse(view.definition));
        }
      }
    }
  }),

  // save the view for the current user.  We get the current view metadata from redux and save it with a Graphql mutation
  graphql(QUERIES[viewType].save, {
    props: ({
      ownProps: {
        intl,
        closePanel,
        selectedFields,
        includeFilters,
        filter,
        sort
      },
      mutate
    }) => ({
      onSubmit(values, dispatch) {
        return mutate({
          variables: {
            name: values.name,
            definition: JSON.stringify({
              fields: selectedFields,
              filter: includeFilters && filter,
              sort: includeFilters && sort
            })
          },
          refetchQueries: [
            {
              query: QUERIES[viewType].get
            }
          ]
        }).then(
          ({data}) => {
            closePanel();
          },
          r => {
            console.warn('Submission error', r);
            return Promise.reject(
              new SubmissionError({
                _error: intl.formatMessage({
                  id: 'shared.there_was_an_error_saving_update'
                })
              })
            );
          }
        );
      }
    })
  }),
  reduxForm({
    form: FORM_NAMES[viewType],
    validate
  }),

  // handler for deleting the view
  withState('deleting', 'setDeleting', false),
  withState('deleteError', 'setDeleteError', ''),
  graphql(QUERIES[viewType].delete, {
    props: ({
      ownProps: {
        intl,
        selectedView,
        setSelectedView,
        setDeleting,
        setDeleteError,
        closePanel
      },
      mutate
    }) => ({
      deleteView(e) {
        e.preventDefault();
        const name = selectedView;
        setDeleting(true);
        mutate({
          variables: {name},
          refetchQueries: [{query: QUERIES[viewType].get}]
        })
          .then(
            () => {
              closePanel();
              setSelectedView('');
            },
            r => {
              console.warn('Submission error', r);
              setDeleteError(
                intl.formatMessage({id: 'shared.could_not_delete_view'})
              );
            }
          )
          .then(() => setDeleting(false));
      }
    })
  })
);
